import React, {useMemo} from 'react';
import {Box, Container, Stack, Typography, useMediaQuery, useTheme} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import Banner from '../components/Banner.js';
import FeedbackCarousel from '../components/FeedbackCarousel.js';
import {imagekitStaticURL} from '../util/util.js';
import {withTitle} from '../title.js';
import withMainContainer from '../components/WithMainContainer.js';
import WhatsAppButton from '../components/WhatsAppButton.js';
import CardContainer from '../components/CardContainer.js';
import StackWithArrow from '../components/StackWithArrow.js';

const WHATSAPP_MESSAGE = `היי סימונה, אשמח לקבל פרטים על שיעורים מכושפים אחד על אחד 🌸`;

const CheckLine = ({children, color='primary', IconComp=CheckIcon, ...rest})=><Stack
  direction="row" alignItems="center" gap={1}>
  <IconComp color={color} {...rest}/>
  {children}
</Stack>;

const GridItem = props=>{
    let {children} = props;
    let theme = useTheme();
    let sm = useMediaQuery(theme.breakpoints.down('sm'));
    let md = useMediaQuery(theme.breakpoints.down('md'));
    let gridSize = useMemo(()=>sm ? '90%' : md ? '40%' : '30%', [sm, md]);
    return <Stack width={gridSize}>
      <Box component={CardContainer} padding={3} flexGrow={1}>
        <Typography>
          {children}
        </Typography>
      </Box>
    </Stack>;
};

const PrivateLessons = ()=>{
    let theme = useTheme();
    let sm = useMediaQuery(theme.breakpoints.down('sm'));
    let md = useMediaQuery(theme.breakpoints.down('md'));
    return <Stack direction="column">
      <Banner image={imagekitStaticURL`header-private-lessons.png`}
        placeholderColor="#d08c53">
        <Stack direction="column" gap={1}>
          <Typography variant="h4" textAlign="center">
            שיעורים מכושפים אחד על אחד
          </Typography>
          <Typography variant="h5" textAlign="center">
            לעבודה עם יכולות רוחניות
          </Typography>
        </Stack>
        <Typography textAlign="center">
          מרגישה שמשהו בתוכך נמשך למה שמעבר?<br/>
          רוצה להבין, לחקור ולחוות את העולם הרוחני והמיסטי מקרוב - בדרך אישית, בטוחה ומעשית?<br/>
          זה המקום הבטוח לשאול את כל השאלות שאין לך את מי לשאול.
        </Typography>
      </Banner>
      <Container maxWidth="lg">
        <Stack direction="column" gap={3} marginTop={3} marginBottom={3}>
          <Stack gap={4} justifyContent="center"
            direction={sm ? 'column' : 'row'}
            alignItems={sm ? 'center' : null}>
            <Box component="img" width="250px" maxHeight="350px"
              src={imagekitStaticURL`simona-magical-forest.jpg`} sx={{
                  objectFit: 'cover',
                  boxShadow: '4px 4px 8px rgba(0, 0, 0, 0.5)',
                  borderRadius: '8px',
            }}/>
            <Stack direction="column" gap={3} justifyContent="center">
              <Typography textAlign={sm ? 'center' : null}>
                מילדות הרגשתי אחרת.<br/>
                הייתה לי אינטואיציה חדה, חלומות צלולים, תחושות בטן שלא טעו - ויכולת לראות אנשים ודברים לעומק, גם כשאחרים לא הבינו.<br/>
                בתור ילדה זה היה קסום - שיתפתי חברות בוויז'נים, בחלומות, בתקשורים. אפילו הגענו לחלימה משותפת.<br/>
                אבל כשגדלתי, גיליתי שהסביבה כבר לא יודעת להכיל את זה. חוויתי חרם, שמועות, ופחד אמיתי מהיכולת שלי.<br/>
                אז סגרתי. הדחקתי. נשבעתי שלא יקראו לי שוב “מכשפה”.
              </Typography>
              <Typography variant="h6" textAlign={sm ? 'center' : null}>
                אבל מתנות אמיתיות לא נעלמות.
              </Typography>
              <Typography textAlign={sm ? 'center' : null}>
                לאורך השנים, הן חזרו. דרך שיחות, מפגשים, חלומות חוזרים על קלפים צבעוניים - עד שבחלום אחד, ראיתי את המילה “טארוט”.<br/>
                כשחיפשתי את הקלפים, מצאתי בדיוק את מה שראיתי.<br/>
                משם הדרך הייתה ברורה: ללמוד, לחקור, ולהבין איך אני יכולה לעזור לאחרים - דרך היכולות האלה שניסיתי להחביא.
              </Typography>
              <Typography variant="h6" textAlign={sm ? 'center' : null}>
                היום אני יודעת - זו לא רק אינטואיציה. זו שפה עתיקה. חוכמה פנימית. מפת חיים.
              </Typography>
            </Stack>
          </Stack>
          <Stack direction="column" justifyContent="center"
            alignItems="center" gap={4}>
            <Typography fontWeight="bold" textAlign="center">
              אם את חווה דברים שאי אפשר להסביר, רואה, מרגישה, יודעת - אבל אין לך איפה לשאול, לשתף או להבין - המקום הזה נוצר בשבילך.
            </Typography>
            <Box component="img" maxWidth="200px"
              src={imagekitStaticURL`moon-divider.png`} sx={{objectFit: 'contain'}}/>
          </Stack>
        </Stack>
      </Container>
      <Stack direction="column" justifyContent="center" gap={3}
        marginTop={2} paddingTop={4} paddingBottom={4}
        sx={{backgroundColor: theme.palette.primary.light}}>
        <Typography variant="h5" textAlign="center">
          על השיעורים
        </Typography>
        <Typography textAlign="center">
          השיעורים המכושפים הם מפגש אישי ומדויק איתי בזום,<br/>
          שבו נוכל לצלול לתוך כל מה שמסקרן, מרגש, מפחיד או פשוט מבקש להיחקר.<br/>
          בין אם את רק סקרנית, כבר עמוק בעולם הרוח - או מרגישה שאת צריכה הכוונה וכלים כדי להבין ולשלוט ביכולות שלך בצורה בטוחה וברורה.
        </Typography>
        <Box margin="auto" padding={2}>
          <CardContainer>
            <Stack direction="column" gap={3} padding={4} maxWidth={600}>
              <Typography variant="h6">
                מה אפשר ללמוד?
              </Typography>
              <Typography>
                אין סילבוס קבוע - כל שיעור נבנה לפי מי שאת ומה שאת מחפשת.<br/>
                הנה חלק מהנושאים שאנחנו יכולות לעבוד עליהם יחד:
              </Typography>
              <CheckLine>
                לתקשר בעזרת מטוטלת או מוטות תקשור
              </CheckLine>
              <CheckLine>
                לפתוח את העין השלישית ולבנות מרחב אנרגטי אישי
              </CheckLine>
              <CheckLine>
                להתחבר למדריך הרוחני ולגשת לספרייה האקשית
              </CheckLine>
              <CheckLine>
                לעבוד עם קמעות, לבצע טיהור וליצור הגנות אנרגטיות
              </CheckLine>
              <CheckLine>
                להבין את המשמעות של טקסים ולבנות טקס אישי שמתאים לך
              </CheckLine>
              <CheckLine>
                להכין נרות מכושפים וברכות שמביאות איזון אנרגטי לשימוש יומיומי
              </CheckLine>
              <CheckLine>
                לחזק את האינטואיציה ולשלוט ביכולות שלך בצורה מודעת, בטוחה וברורה
              </CheckLine>
            </Stack>
          </CardContainer>
        </Box>
        <Typography variant="h6" textAlign="center">
          וזה רק חלק קטן - כל שיעור נבנה מתוך הקשבה למה שהנשמה שלך מבקשת עכשיו.
        </Typography>
      </Stack>
      <Container maxWidth="lg">
        <Stack direction="column" gap={3} justifyContent="center"
          alignItems="center" marginTop={2} marginBottom={7}>
          <Typography variant="h5" textAlign="center" marginTop={3}>
            עם מה תצאי?
          </Typography>
          <Stack direction="row" gap={2} flexWrap="wrap"
            justifyContent="center">
            <GridItem>
              כלים פרקטיים שתוכלי להשתמש בהם מיד: תקשורת עם ההדרכה שלך, שיטות טיהור, תרגולים אנרגטיים, או כל דבר שנעבוד עליו יחד
            </GridItem>
            <GridItem>
              הבנה רחבה יותר של מה קורה לך - ולמה זה קורה
            </GridItem>
            <GridItem>
              ביטחון - לדעת שאת לא לבד, ושיש שפה לידע הפנימי שכבר קיים בך
            </GridItem>
            <GridItem>
              מרחב שמאפשר לך לחקור את מי שאת - בלי להתנצל
            </GridItem>
            <GridItem>
              התגברות על פחדים ואמונות מגבילות
            </GridItem>
            <GridItem>
              אימון וידע שיאפשרו לך לשלוט ביכולות שלך - ובכך לעזור לעצמך ולאחרים
            </GridItem>
          </Stack>
        </Stack>
      </Container>
      <Banner image={imagekitStaticURL`banner-witchy.png`}
        placeholderColor="#242328">
        <Typography variant="h5">
          איך זה עובד?
        </Typography>
        <Stack direction="column" gap={2} alignItems="start" justifyContent="start">
          <CheckLine color="contrastText">
            השיעורים מתקיימים בזום - באורך של שעה או כשעתיים (תלוי בנושא)
          </CheckLine>
          <CheckLine color="contrastText">
            אפשר לקבוע שיעור חד־פעמי, או סדרת שיעורים לפי הקצב שלך
          </CheckLine>
          <CheckLine color="contrastText">
            אין צורך בניסיון קודם - רק פתיחות, סקרנות וקריאה פנימית
          </CheckLine>
          <CheckLine color="contrastText">
            אני כאן כדי להחזיק עבורך מרחב נקי, מכיל ובטוח - בלי ציניות, בלי שיפוט
          </CheckLine>
          <CheckLine color="contrastText">
            וגם אם את עדיין לא בטוחה אם את מוכנה - זה לגמרי בסדר. השיעור נבנה יחד איתך, בקצב שמתאים לך.
          </CheckLine>
        </Stack>
      </Banner>
      <StackWithArrow backgroundColor={theme.palette.primary.light} arrowIndex={1}>
        <Container maxWidth="md" sx={{zIndex: 2}}>
          <Stack gap={3} justifyContent="center"
            direction={sm ? 'column' : 'row'}
            alignItems={sm ? 'center' : null}
            marginTop={4} marginBottom={6}>
            <Stack direction="column" gap={4}>
              <Typography variant="h5" textAlign={sm ? 'center' : null}>
                למי זה מתאים?
              </Typography>
              <CheckLine IconComp={CircleOutlinedIcon} color="black" sx={{fontSize: 14}}>
                למי שמרגישה משיכה לעולם הרוחני, ורוצה להבין, ללמוד ולתרגל
              </CheckLine>
              <CheckLine IconComp={CircleOutlinedIcon} color="black" sx={{fontSize: 14}}>
                למי שכבר חוותה דברים "מעבר", ורוצה כלים להבין מה קורה ולשלוט בזה
              </CheckLine>
              <CheckLine IconComp={CircleOutlinedIcon} color="black" sx={{fontSize: 14}}>
                למי שמחפשת שפה, עומק וסדר בעולם שפעמים רבות מרגיש מבולגן או מוזר
              </CheckLine>
              <CheckLine IconComp={CircleOutlinedIcon} color="black" sx={{fontSize: 14}}>
                ולכל מי שמבקשת מקום בטוח לדבר בו על מה שלא תמיד אפשר לשתף - ולהיזכר בכוח שיש בה
              </CheckLine>
            </Stack>
            <Box component="img" height="150px"
              src={imagekitStaticURL`round-hand-1.png`} sx={{objectFit: 'contain'}}/>
          </Stack>
        </Container>
      </StackWithArrow>
      <Container maxWidth="md">
        <Stack direction="column" gap={3} alignItems="center"
          marginTop={7} marginBottom={4}>
          <Typography variant="h4" textAlign="center" marginTop={3}>
            מוכנה לפתח את היכולות שלך?
          </Typography>
          <Typography textAlign="center">
            אני כאן בשבילך - שלחי לי הודעה ונבנה יחד שיעור שמתאים בדיוק לך.
          </Typography>
          <WhatsAppButton message={WHATSAPP_MESSAGE} text="לקביעת שיחת התאמה ללא עלות"/>
        </Stack>
      </Container>
      <Stack width="100%" sx={{backgroundColor: theme.palette.primary.dark}} marginTop={4} paddingBottom={4}>
        <Container maxWidth="lg">
          <Typography color={theme.palette.primary.contrastText} align="center" variant="h3" marginTop={4}>
            מה לקוחות מספרות
          </Typography>
          <FeedbackCarousel tags="witchcraft-feedback"/>
        </Container>
        <Stack justifyContent="center" alignItems="center" marginBottom={4}>
          <WhatsAppButton message={WHATSAPP_MESSAGE} text="לקביעת שיחת התאמה ללא עלות" variant="contained"/>
        </Stack>
      </Stack>
    </Stack>;
};

export default withMainContainer(withTitle(`סימונה צ'רי - שיעורים מכושפים אחד על אחד`, PrivateLessons), {headerMargin: 0});
